/**
 * Sound effects manager for the blackjack trainer
 * Uses Web Audio API for responsive, low-latency sounds
 */

// Audio context singleton
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  }
  return audioContext;
}

// Sound enabled state (will be synced with settings)
let soundEnabled = true;

export function setSoundEnabled(enabled: boolean): void {
  soundEnabled = enabled;
}

export function isSoundEnabled(): boolean {
  return soundEnabled;
}

/**
 * Play a synthesized beep sound
 */
function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.3
): void {
  if (!soundEnabled) return;
  
  try {
    const ctx = getAudioContext();
    
    // Resume context if suspended (browsers require user interaction)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    
    // Fade in and out to avoid clicks
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  } catch (e) {
    console.warn('Sound playback failed:', e);
  }
}

/**
 * Play a chord (multiple frequencies)
 */
function playChord(
  frequencies: number[],
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.2
): void {
  frequencies.forEach(freq => playTone(freq, duration, type, volume / frequencies.length));
}

// === Sound Effect Functions ===

/**
 * Card deal sound - realistic card dealing sound
 */
export function playCardDeal(): void {
  if (!soundEnabled) return;

  try {
    const ctx = getAudioContext();

    // Resume context if suspended (browsers require user interaction)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    // Create a more realistic card sound using multiple components
    const duration = 0.08;

    // Main card slap sound - low frequency with quick attack
    const mainOsc = ctx.createOscillator();
    const mainGain = ctx.createGain();

    mainOsc.connect(mainGain);
    mainGain.connect(ctx.destination);

    mainOsc.frequency.setValueAtTime(120, ctx.currentTime); // Low frequency for the "slap"
    mainOsc.type = 'sawtooth';

    // Quick attack and decay
    mainGain.gain.setValueAtTime(0, ctx.currentTime);
    mainGain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.01);
    mainGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    mainOsc.start(ctx.currentTime);
    mainOsc.stop(ctx.currentTime + duration);

    // Add a subtle higher frequency component for texture
    const textureOsc = ctx.createOscillator();
    const textureGain = ctx.createGain();

    textureOsc.connect(textureGain);
    textureGain.connect(ctx.destination);

    textureOsc.frequency.setValueAtTime(800, ctx.currentTime);
    textureOsc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + duration);
    textureOsc.type = 'square';

    textureGain.gain.setValueAtTime(0, ctx.currentTime);
    textureGain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.005);
    textureGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    textureOsc.start(ctx.currentTime);
    textureOsc.stop(ctx.currentTime + duration);

  } catch (e) {
    console.warn('Sound playback failed:', e);
  }
}

/**
 * Card flip sound - slightly longer
 */
export function playCardFlip(): void {
  playTone(600, 0.08, 'triangle', 0.2);
}

/**
 * Win sound - ascending happy tones
 */
export function playWin(): void {
  if (!soundEnabled) return;
  
  setTimeout(() => playTone(523, 0.15, 'sine', 0.25), 0);    // C5
  setTimeout(() => playTone(659, 0.15, 'sine', 0.25), 100);  // E5
  setTimeout(() => playTone(784, 0.2, 'sine', 0.3), 200);    // G5
}

/**
 * Blackjack sound - even more celebratory
 */
export function playBlackjack(): void {
  if (!soundEnabled) return;
  
  setTimeout(() => playTone(523, 0.1, 'sine', 0.25), 0);     // C5
  setTimeout(() => playTone(659, 0.1, 'sine', 0.25), 80);    // E5
  setTimeout(() => playTone(784, 0.1, 'sine', 0.25), 160);   // G5
  setTimeout(() => playTone(1047, 0.3, 'sine', 0.35), 240);  // C6
}

/**
 * Loss sound - descending sad tones
 */
export function playLoss(): void {
  if (!soundEnabled) return;
  
  setTimeout(() => playTone(440, 0.2, 'sine', 0.2), 0);      // A4
  setTimeout(() => playTone(349, 0.3, 'sine', 0.2), 150);    // F4
}

/**
 * Push sound - neutral tone
 */
export function playPush(): void {
  playTone(440, 0.2, 'triangle', 0.2);  // A4
}

/**
 * Bust sound - harsh descending
 */
export function playBust(): void {
  if (!soundEnabled) return;
  
  playTone(300, 0.15, 'sawtooth', 0.15);
  setTimeout(() => playTone(200, 0.25, 'sawtooth', 0.15), 100);
}

/**
 * Correct count sound - pleasant ding
 */
export function playCorrect(): void {
  playChord([523, 659], 0.15, 'sine', 0.3);  // C5 + E5 (major third)
}

/**
 * Incorrect count sound - buzzer
 */
export function playIncorrect(): void {
  playTone(200, 0.2, 'square', 0.2);
}

/**
 * Button click sound - subtle feedback
 */
export function playClick(): void {
  playTone(1000, 0.03, 'sine', 0.1);
}

/**
 * Chip/bet sound - coin-like
 */
export function playChip(): void {
  if (!soundEnabled) return;
  
  playTone(2000, 0.03, 'square', 0.1);
  setTimeout(() => playTone(2500, 0.03, 'square', 0.08), 30);
}

/**
 * Shuffle sound - multiple quick sounds
 */
export function playShuffle(): void {
  if (!soundEnabled) return;
  
  for (let i = 0; i < 5; i++) {
    setTimeout(() => playTone(400 + Math.random() * 200, 0.04, 'triangle', 0.1), i * 50);
  }
}

/**
 * Insurance sound - warning-like
 */
export function playInsurance(): void {
  playTone(600, 0.1, 'sine', 0.2);
  setTimeout(() => playTone(600, 0.1, 'sine', 0.2), 150);
}

/**
 * Split sound - splitting effect
 */
export function playSplit(): void {
  if (!soundEnabled) return;
  
  playTone(500, 0.08, 'triangle', 0.2);
  setTimeout(() => playTone(700, 0.08, 'triangle', 0.2), 80);
}

/**
 * Achievement unlocked sound - fanfare
 */
export function playAchievement(): void {
  if (!soundEnabled) return;
  
  setTimeout(() => playTone(523, 0.1, 'sine', 0.25), 0);     // C5
  setTimeout(() => playTone(659, 0.1, 'sine', 0.25), 100);   // E5
  setTimeout(() => playTone(784, 0.1, 'sine', 0.25), 200);   // G5
  setTimeout(() => playChord([1047, 1319, 1568], 0.4, 'sine', 0.3), 300);  // C6 chord
}

/**
 * Timer warning sound - urgent beep
 */
export function playTimerWarning(): void {
  playTone(880, 0.1, 'square', 0.2);  // A5
}

/**
 * Speed drill complete sound
 */
export function playDrillComplete(): void {
  if (!soundEnabled) return;
  
  setTimeout(() => playTone(392, 0.15, 'sine', 0.25), 0);    // G4
  setTimeout(() => playTone(494, 0.15, 'sine', 0.25), 150);  // B4
  setTimeout(() => playTone(587, 0.15, 'sine', 0.25), 300);  // D5
  setTimeout(() => playTone(784, 0.3, 'sine', 0.3), 450);    // G5
}

